import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { apiGet } from "./api";

export type Permission =
  | "view_bookings"
  | "create_booking"
  | "cancel_booking"
  | "edit_booking"
  | "manage_blocks"
  | "manage_closures"
  | "view_revenue"
  | "override_booking_limits";

export interface MeUser {
  id: string;
  email: string;
  role: "SUPERADMIN" | "MANAGER" | "STAFF" | "COACH";
  firstName: string;
  lastName: string;
  phone: string;
  preferredLanguage: string;
  clubId: string | null;
  inviteStatus: string;
  // STAFF-only; null for MANAGER/SUPERADMIN (they bypass checks).
  permissions: Partial<Record<Permission, boolean>> | null;
}

// Mirror of the server-side check in apps/api/src/lib/permissions.ts.
export function hasPermission(
  user: MeUser | null | undefined,
  perm: Permission,
): boolean {
  if (!user) return false;
  if (user.role === "SUPERADMIN" || user.role === "MANAGER") return true;
  return Boolean(user.permissions?.[perm]);
}

export interface MeSport {
  id: string;
  name: string;
  sessionDurationMinutes: number;
}

export interface MeCourt {
  id: string;
  name: string;
  nameEn: string | null;
  nameRu: string | null;
  sportId: string;
  isIndoor: boolean;
  surface: string | null;
  size: string | null;
  pricePerSession: string;
  workingHoursStart: string | null;
  workingHoursEnd: string | null;
  displayOrder: number | null;
  isActive: boolean;
  // Splittable courts (basketball "two halves"). On a whole court,
  // `isSplittable` is true and its two halves appear as separate MeCourt rows
  // with `parentCourtId` pointing back at it (and their own half price).
  parentCourtId: string | null;
  isSplittable: boolean;
}

export type SubscriptionTier = "BASIC" | "PRO" | "ENTERPRISE";

export interface MeClub {
  id: string;
  slug: string;
  name: string;
  // Optional translations of the club name surfaced to public web visitors
  // based on their locale. The primary `name` stays the canonical label.
  nameEn: string | null;
  nameRu: string | null;
  address: string;
  city: string | null;
  country: string;
  lat: number | null;
  lng: number | null;
  phone: string | null;
  website: string | null;
  // Subheading shown below the club title on the public booking page (and used
  // as the <meta description> for SEO). `descriptionSr` is treated as the
  // primary value; `descriptionEn`/`descriptionRu` are optional translations.
  description: string | null;
  descriptionSr: string | null;
  descriptionEn: string | null;
  descriptionRu: string | null;
  // Multilingual house rules edited via the TipTap editor on /club-rules.
  // Sanitised HTML; `rulesSr` is the canonical Serbian value with `rules` as
  // the primary fallback.
  rules: string | null;
  rulesSr: string | null;
  rulesEn: string | null;
  rulesRu: string | null;
  workingHoursStart: string;
  workingHoursEnd: string;
  bookingDaysAdvance: number;
  // When true the club doesn't charge — hide every price/revenue surface.
  isFree: boolean;
  setupComplete: boolean;
  // All bookings require an approved membership. Implies requireLogin.
  requireMembership: boolean;
  allowMemberOnlySlots: boolean;
  // Coached sessions enabled (PRO+). Drives the Coaches nav + the public
  // Coaching tab.
  allowCoaching: boolean;
  feedbackEnabled: boolean;
  // ISO timestamp the club is paid through, or null (never subscribed).
  subscribedUntil: string | null;
  subscriptionTier: SubscriptionTier;
  // SUPERADMIN-set cap on court count. Null = unlimited.
  maxCourts: number | null;
  sports: MeSport[];
  courts: MeCourt[];
}

// Derived from MeClub.subscribedUntil — null is treated as expired. SUPERADMIN
// bypasses the gate server-side but for them this still reflects the club's
// real state so banners aren't shown for inactive clubs they're inspecting.
export function isClubSubscriptionActive(
  club: { subscribedUntil: string | null } | null | undefined,
): boolean {
  if (!club || !club.subscribedUntil) return false;
  const expiresAt = new Date(club.subscribedUntil).getTime();
  return Number.isFinite(expiresAt) && expiresAt > Date.now();
}

interface MeContextValue {
  user: MeUser | null;
  club: MeClub | null;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const MeContext = createContext<MeContextValue | null>(null);

export function MeProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<MeUser | null>(null);
  const [club, setClub] = useState<MeClub | null>(null);
  // `isLoading` only flips for the initial fetch. Subsequent `refresh()` calls
  // update state in place so descendants don't unmount mid-page (which would
  // otherwise drop UI state like SUPERADMIN's selected club and trigger
  // redundant `useSession` fetches on remount).
  const [isLoading, setIsLoading] = useState(true);
  const initialLoadedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!initialLoadedRef.current) setIsLoading(true);
    try {
      const data = await apiGet<{ user: MeUser; club: MeClub | null }>(
        "/api/admin/me",
      );
      setUser(data.user);
      setClub(data.club);
    } catch {
      setUser(null);
      setClub(null);
    } finally {
      initialLoadedRef.current = true;
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <MeContext.Provider value={{ user, club, isLoading, refresh }}>
      {children}
    </MeContext.Provider>
  );
}

export function useMe(): MeContextValue {
  const ctx = useContext(MeContext);
  if (!ctx) throw new Error("useMe must be used inside MeProvider");
  return ctx;
}

// True when the current admin can perform writes against their club. SUPERADMIN
// always returns true (server bypasses the gate for them); MANAGER/STAFF return
// false when the club's subscription has lapsed or was never set.
export function useSubscriptionActive(): boolean {
  const { user, club } = useMe();
  if (!user) return false;
  if (user.role === "SUPERADMIN") return true;
  return isClubSubscriptionActive(club);
}
